import { Button } from "nvs-design-system/dist/Button_DEPRECATED";
import StyledCheckbox from "@/src/components/form/checkbox/StyledCheckbox";
import { ArrowRight } from "nvs-ds-icons/react/navigation";
import ActionModal, {
  ActionModalContentWrapper,
  ActionModalWrapper,
} from "@/src/components/Modal/ActionModal";
import RichTextRenderer from "@/src/components/RichTextRenderer";
import DefaultRichTextComponentOptions from "@/src/components/RichTextRenderer/DefaultRichTextComponentOptions";
import { Body5 } from "@/src/components/typography/components/Body";
import clsx from "clsx";
import React, { useState } from "react";
import useRequest3DDownloadMutation from "../../../hooks/useRequest3DDownloadMutation";
import { useRedemptionModalContext } from "./RedemptionModalContext";
import { useRedemptionToast } from "../hooks/useRedemptionToast";
import {
  RedemptionBody,
  RedemptionSubhead,
} from "nvs-types/dist/types/generated/contentful";
import {
  getRedemptionErrorToastMsg,
  getRedemptionSuccessToastMsg,
} from "../helpers/RedemptionToastHelpers";
import { ScrollWindow } from "@/src/components/ScrollWindow";
import {
  useDownloadReadyAnalytics,
  usePrepareDownloadClickedAnalytics,
} from "../hooks/analyticsHooks";
import { MINT_CHAIN_ID } from "@/libs/apis/onboarding/minting/constants";

export type RedemptionModalProps = {
  headline: string;
  asset: string;
  name: string;
  subHead: Pick<RedemptionSubhead, "json">;
  body: Pick<RedemptionBody, "json">;
  legalRedemptionLink: string;
  tokenId: string;
  collectionSlug: string;
  tokenName: string;
  ownedBy: string;
  contractAddress: string;
};

export const RedemptionModal = ({
  headline,
  asset,
  name,
  subHead,
  body,
  legalRedemptionLink,
  tokenId,
  collectionSlug,
  tokenName,
  ownedBy,
  contractAddress,
}: RedemptionModalProps) => {
  const createToast = useRedemptionToast();
  const [isLegalChecked, setIsLegalChecked] = useState<boolean>(false);
  const { isOpen, setIsOpen } = useRedemptionModalContext();
  const { mutate, isLoading } = useRequest3DDownloadMutation();
  const prepareDownloadClickedAnalytics = usePrepareDownloadClickedAnalytics();
  const downloadReadyAnalytics = useDownloadReadyAnalytics();

  const onPrepareDownloadClicked = () => {
    prepareDownloadClickedAnalytics({
      token: {
        tokenId,
        name: tokenName,
        ownerHandle: ownedBy,
        isListed: false, // TODO: Update this when we have data coming from marketplace
      },
      collections: {
        name: collectionSlug,
        contractAddress: contractAddress,
        chainId: parseInt(MINT_CHAIN_ID), // chain Id does not change per env
        slug: collectionSlug,
      },
    });

    mutate(
      {
        tokenId: tokenId,
        collectionSlug: collectionSlug,
        legalURL: legalRedemptionLink,
      },
      {
        onSuccess: (res) => {
          downloadReadyAnalytics({
            token: {
              tokenId,
              name: tokenName,
              ownerHandle: ownedBy,
              isListed: false, // TODO: Update this when we have data coming from marketplace
            },
            collections: {
              name: collectionSlug,
              contractAddress: contractAddress,
              chainId: parseInt(MINT_CHAIN_ID), // chain Id does not change per env
              slug: collectionSlug,
            },
          });
          createToast(true, getRedemptionSuccessToastMsg(res.expiration));
        },
        onError: () => createToast(false, getRedemptionErrorToastMsg()),
        onSettled: () => {
          setIsOpen(false);
          setIsLegalChecked(false);
        },
      },
    );
  };
  return (
    <ActionModal
      title={headline}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <ActionModalWrapper
        onSubmit={(e) => {
          e.preventDefault();
          onPrepareDownloadClicked();
        }}
      >
        <ScrollWindow axis="y">
          <ActionModalContentWrapper className="flex-col flex gap-4 md:max-h-[70vh]">
            <picture>
              <img src={asset} alt={name} className="border border-gray-425" />
            </picture>
            <Body5 className="text-white uppercase">{name}</Body5>
            <RichTextRenderer
              richText={subHead}
              options={DefaultRichTextComponentOptions}
            />
            <StyledCheckbox
              id="legal-checkbox"
              name="legal-checkbox"
              checked={isLegalChecked}
              required
              onChange={(e) => setIsLegalChecked(e.currentTarget.checked)}
            >
              <RichTextRenderer
                richText={body}
                options={DefaultRichTextComponentOptions}
              />
            </StyledCheckbox>
          </ActionModalContentWrapper>
        </ScrollWindow>
        <Button
          type="submit"
          className={clsx("w-full")}
          icon={<ArrowRight />}
          isLoading={isLoading}
          disabled={!isLegalChecked || isLoading}
          data-testid="downloadLink"
        >
          PREPARE DOWNLOAD
        </Button>
      </ActionModalWrapper>
    </ActionModal>
  );
};
